import React, { useMemo } from 'react';
import { Box, Stack } from '@mui/material';
import { TextView, ChartTooltip } from '../../atoms';
import { DateTime } from 'luxon';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer
} from 'recharts';
import { useEngagementOverTime } from '@api';
import { SkeletonChart } from '@organisms';
import Team from '../../pages/admin/team/index';

interface CustomizedAxisTickProps {
  x: number;
  y: number;
  payload: { value: any };
}

interface EngagementOverTimeProps {
  start?: Date;
  end?: Date;
  companyId?: string;
  team?: Team;
}

const EngagementOverTime: React.FC<EngagementOverTimeProps> = ({
  start,
  end,
  companyId,
  team
}: EngagementOverTimeProps) => {
  const startDate = useMemo(
    () =>
      start
        ? DateTime.fromJSDate(start).toISO({
            suppressMilliseconds: true,
            includeOffset: false
          })
        : undefined,
    [start]
  );
  const endDate = useMemo(
    () =>
      end
        ? DateTime.fromJSDate(end).toISO({
            suppressMilliseconds: true,
            includeOffset: false
          })
        : undefined,
    [end]
  );

  const { data: response, isFetching: loading } = useEngagementOverTime({
    from: startDate,
    to: endDate,
    companyId,
    teamId: team?.id
  });
  const engagementOverTime = response?.data;

  const aggregation = engagementOverTime?.data.aggregation ?? 'days';
  const values = engagementOverTime?.data.values ?? [];

  const CustomizedAxisTick = (props: CustomizedAxisTickProps) => {
    const { x, y, payload } = props;
    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="end"
          fill="#666"
          transform="rotate(-35)"
          style={{ fontFamily: 'Montserrat', fontSize: 8, fontWeight: 400 }}
        >
          {DateTime.fromISO(payload.value).toFormat(
            aggregation === 'days' ? 'dd/LL/yyyy' : 'LLL, yyyy'
          )}
        </text>
      </g>
    );
  };

  const CustomizedYAxisTick = (props: CustomizedAxisTickProps) => {
    const { x, y, payload } = props;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          textAnchor="end"
          fill="#666"
          style={{ fontFamily: 'Montserrat', fontSize: 10, fontWeight: 400 }}
        >
          {payload.value}
        </text>
      </g>
    );
  };

  if (loading && values.length === 0) {
    return (
      <Box sx={{ p: 1 }}>
        <SkeletonChart height={330} legend legendItems={1} />
      </Box>
    );
  }

  return (
    <Box
      sx={{
        backgroundColor: 'rgba(124, 58, 237, 0.06)',
        border: '1px solid rgba(124, 58, 237, 0.15)',
        p: 1,
        borderRadius: 2,
        height: 350
      }}>
      <TextView $fontSize={24} sx={{ marginLeft: '20px', color: 'black' }}>
        Engagement Over Time
      </TextView>
      <Stack direction={'row'} alignItems={'center'} justifyContent={'center'} sx={{ mt: 2 }}>
        <Box sx={{ width: '24px', height: '4px', backgroundColor: '#086BDE', mr: 1 }} />
        <TextView $fontSize={12} $fontWeight={400}>
          Total Messages
        </TextView>
      </Stack>
      <ResponsiveContainer width="97%" height="80%">
        <LineChart data={values}>
          <YAxis tick={(props) => CustomizedYAxisTick(props)} />
          <CartesianGrid strokeDasharray="3 3" />
          <Tooltip
            content={
              <ChartTooltip
                nameMap={{ messages: 'Total Messages' }}
                labelFormatter={(label) => {
                  const dt = DateTime.fromISO(String(label));
                  if (!dt.isValid) return String(label);
                  return aggregation === 'days'
                    ? dt.toFormat('ccc, dd LLL yyyy')
                    : dt.toFormat('LLLL yyyy');
                }}
              />
            }
            cursor={{ stroke: '#086BDE', strokeWidth: 1, strokeDasharray: '4 4', opacity: 0.5 }}
          />
          <XAxis
            dataKey="timestamp"
            height={60}
            interval={0}
            tick={(props) => CustomizedAxisTick(props)}
          />
          <Line type="monotone" dataKey="messages" stroke="#086BDE" strokeWidth={2} />
        </LineChart>
      </ResponsiveContainer>
    </Box>
  );
};

export default EngagementOverTime;
